import { ethers } from "ethers";
import { Box, Stack, Typography } from "@mui/material";

import {
  MODULO_COINFLIP,
  MODULO_ETHEROLL,
  MODULO_ROLLADICE,
  MODULO_ROULETTE,
  MODULO_TWODICE,
} from "./games";

const MAX_MASK_MODULO = 40;
const HOUSE_EDGE_PERCENT = ethers.BigNumber.from("1"); // 1%

export function FormatGui({ wei, network }) {
  let value = parseFloat(ethers.utils.formatEther(wei)).toFixed(
    network.ui.decimals
  );
  return (
    <span style={{ display: "inline-block", whiteSpace: "nowrap" }}>
      <img
        src={network.ui.curIconUrl}
        height={24}
        style={{ verticalAlign: "middle", paddingRight: "0.25rem" }}
      />
      {value}
    </span>
  );
}

export function parseAmount(wei, network) {
  return parseFloat(ethers.utils.formatEther(wei)).toFixed(network.ui.decimals);
}

export function FormatN2W({ amount }) {
  if (amount) {
    return (
      <span style={{ display: "inline-block" }}>
        <Box
          component="img"
          src="/img/n2w.svg"
          width="24px"
          alt=""
          sx={{ verticalAlign: "middle", mr: 0.5 }}
        />
        {amount.toFixed(2)}{" "}
      </span>
    );
  }
}

export const truncateAddress = (address) => {
  if (!address) return "No Account";
  const match = address.match(
    /^(0x[a-zA-Z0-9]{2})[a-zA-Z0-9]+([a-zA-Z0-9]{2})$/
  );
  if (!match) return address;
  return `${match[1]}…${match[2]}`;
};

export const toHex = (num) => {
  const val = Number(num);
  return "0x" + val.toString(16);
};

export function getWinningChance(game, inputs) {
  let odds;
  if (game.modulo === MODULO_COINFLIP) {
    odds = 0.5;
  } else if (game.modulo === MODULO_ROLLADICE) {
    odds = Object.keys(inputs).length / 6;
  } else if (game.modulo === MODULO_TWODICE) {
    let combinations = 0.0;
    const table = [1, 2, 3, 4, 5, 6, 5, 4, 3, 2, 1];
    inputs.forEach((dice) => {
      combinations += table[parseInt(dice - 2, 10)];
    });
    odds = combinations / 36;
  } else if (game.modulo === MODULO_ROULETTE) {
    odds = Object.keys(inputs).length / 37;
  } else if (game.modulo === MODULO_ETHEROLL) {
    odds = inputs[0] / 100;
  }
  return odds;
}

export function getDiceWinAmount(network, inputs, amountWei, modulo) {
  if (inputs.length === 0) {
    return ethers.BigNumber.from(0);
  }
  const betMask = getBetMask(modulo, inputs);
  const rollUnder = getRollUnder(betMask, modulo);
  if (!rollUnder) {
    // game not matching inputs during sync
    return ethers.BigNumber.from(0);
  }
  const { houseEdgeMinimumAmount, minJackpotBet, jackpotFee } =
    network.contract;

  const amountWeiBN = ethers.BigNumber.from(amountWei);

  let houseEdgeBN = amountWeiBN
    .mul(HOUSE_EDGE_PERCENT)
    .div(ethers.BigNumber.from(100));
  if (houseEdgeBN.lt(houseEdgeMinimumAmount)) {
    houseEdgeBN = houseEdgeMinimumAmount;
  }

  const currentJackpotFeeBN = amountWeiBN.gte(minJackpotBet)
    ? jackpotFee
    : ethers.BigNumber.from(0);

  const par1 = amountWeiBN.sub(houseEdgeBN).sub(currentJackpotFeeBN);

  const winAmount = par1
    .mul(ethers.BigNumber.from(modulo))
    .div(ethers.BigNumber.from(rollUnder));

  return winAmount;
}

export function getBetMask(modulo, selected) {
  let betMask = 0;
  if (modulo === MODULO_COINFLIP) {
    if (selected.includes(1)) {
      betMask = 1;
    } else {
      betMask = 2;
    }
  } else if (modulo === MODULO_ROLLADICE || modulo === MODULO_ROULETTE) {
    selected.forEach((dice) => {
      betMask = BigInt(betMask) | (BigInt(1) << BigInt(dice));
    });
  } else if (modulo === MODULO_TWODICE) {
    selected.forEach((dice) => {
      for (var a = 1; a <= 6; a++) {
        for (var b = 1; b <= 6; b++) {
          if (a + b === dice) {
            const diceLayer = Math.pow(2, 6 * (a - 1) + (b - 1));
            betMask = betMask + diceLayer;
          }
        }
      }
    });
  } else if (modulo === MODULO_ETHEROLL) {
    betMask = selected[0];
  }
  return ethers.BigNumber.from(BigInt(betMask));
}

export function getRollUnder(betMask, modulo) {
  if (modulo <= MAX_MASK_MODULO) {
    return bitCount(dec2bin(betMask));
  } else {
    return betMask.toNumber();
  }
}

function bitCount(n) {
  return n.toString(2).match(/1/g)?.length;
}

export function dec2bin(dec) {
  return `${(BigInt(dec) >> BigInt(0)).toString(2)}`;
}

export function getRange(lowEnd, highEnd) {
  var list = [];
  for (var i = lowEnd; i <= highEnd; i++) {
    list.push(i);
  }
  return list;
}

export function getRouletteColor(n) {
  if (n === 0) {
    return "#72b729"; // green
  } else if (
    (((n >= 1 && n <= 10) || (n >= 19 && n <= 28)) && n % 2) ||
    (((n >= 11 && n <= 18) || (n >= 29 && n <= 36)) && n % 2 === 0)
  ) {
    return "#d41e1d"; // red
  } else {
    return "#16131b"; // black
  }
}

export async function getData(url = "") {
  try {
    const response = await fetch(url);
    return response.json();
  } catch (err) {
    return Promise.reject(err);
  }
}

export async function postData(url = "", data = {}) {
  try {
    // Default options are marked with *
    const response = await fetch(url, {
      method: "POST", // *GET, POST, PUT, DELETE, etc.
      // mode: "cors", // no-cors, *cors, same-origin
      // cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      // credentials: "same-origin", // include, *same-origin, omit
      headers: {
        "Content-Type": "application/json",
        // 'Content-Type': 'application/x-www-form-urlencoded',
      },
      redirect: "follow", // manual, *follow, error
      referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
      body: JSON.stringify(data), // body data type must match "Content-Type" header
    });
    return response.json(); // parses JSON response into native JavaScript objects
  } catch (err) {
    return Promise.reject(err);
  }
}

export function validateEmail(email) {
  // This regular expression tests for the presence of @ and . characters,
  // as well as the position of these characters.
  var emailRegex =
    /^([a-zA-Z0-9_\.\-])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4})+$/;

  // This if statement tests the email address against the regular expression
  // and returns true if the email is valid, and false if it is not.
  if (emailRegex.test(email)) {
    return true;
  } else {
    return false;
  }
}

export function validateUsername(username) {
  return typeof username === "string" && username.length >= 3;
}
